import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import {LLMToolCallBlock, LLMToolResultBlock} from 'llm/chat-library/messages/MessageTypes';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import {DeepResearchToolType, ProgressToolCall, isMCPTool} from 'llm/deep-research/tool-details/types';
import {getToolCall} from 'llm/deep-research/tool-details/utils';

type ChatSessionNode = ExtractByTypename<ChatSessionQuery['chatSession'], 'ChatSessionNode'>;

/**
 * Converts a ProgressDataResponse from the backend to the ProgressData format
 * expected by the frontend components.
 */
export function convertProgressDataResponseToProgressData(chatSessionQuery: ChatSessionQuery): ProgressData {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return {
      historyThread: [],
      numSources: 0,
    };
  }

  const historyThread = getProgressToolCalls(chatSessionQuery.chatSession);

  // TODO (BNCH-126864): Add other sources to progress data
  const numSources = 0;

  return {
    historyThread,
    numSources,
  };
}

const toolsToInclude = [
  DeepResearchToolType.SearchTool,
  DeepResearchToolType.SearchNotebookEntries,
  DeepResearchToolType.SearchSequencesTool,
  DeepResearchToolType.GetWarehouseOverviewTool,
  DeepResearchToolType.QueryWarehouseTool,
  DeepResearchToolType.ReadBenchlingDocumentTool,
  DeepResearchToolType.AttachmentTool,
  DeepResearchToolType.PublicDataSubAgent,
  DeepResearchToolType.IdsToModelsTool,
  DeepResearchToolType.PlottingTool,
];

export function getProgressToolCalls(chatSession: ChatSessionNode): Array<ProgressToolCall> {
  // We assume that tool calls will always precede their corresponding tool results.
  const toolMap = new Map<string, [LLMToolCallBlock, LLMToolResultBlock | null, Date]>();

  chatSession.allThreads.forEach((thread) => {
    thread.events.forEach((event) => {
      if (event.__typename === 'LLMMessageChatEventNode') {
        const createdAt = new Date(event.createdAt);
        event.messageBlocks.forEach(({content: block}) => {
          if (block.type === 'tool_call') {
            const toolName = block.value.name;
            // Include tool if it's in the known list OR if it's an MCP tool
            const shouldInclude =
              toolsToInclude.includes(toolName as DeepResearchToolType) ||
              (typeof toolName === 'string' && isMCPTool(toolName));

            if (shouldInclude) {
              toolMap.set(block.value.id, [block, null, createdAt]);
            }
          } else if (block.type === 'tool_result') {
            const existing = toolMap.get(block.value.id);
            if (existing) {
              const [toolCall, _, date] = existing;
              toolMap.set(block.value.id, [toolCall, block, date]);
            }
          }
        });
      }
    });
  });

  // TODO (BNCH-126793): Add tool summarized tool calls to frontend
  return Array.from(toolMap.values())
    .map(([toolCall, toolResult, createdAt]) => getToolCall(toolCall, toolResult, createdAt))
    .filter((toolCall): toolCall is ProgressToolCall => toolCall != null)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}
